import { ArrowOutward, Call } from "@ellaandil/ui";
import Image from "next/image";
import { forwardRef, type ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { Link } from "@/components/ui/Link";
import { Typography } from "@/components/ui/Typography";
import { Link as NextLink } from "@/i18n/routing";

export type StoreCardLinkVariant = "external" | "phone";

export interface StoreCardLink {
	label: string;
	href: string;
	variant?: StoreCardLinkVariant;
	target?: "_blank" | "_self";
}

export interface StoreCardProps {
	address: ReactNode;
	hours?: string[];
	links?: StoreCardLink[];
	image?: { src: string; alt?: string };
	/**
	 * Pass the layout-specific value for `<img sizes>` so Next/Image picks an
	 * adequately large source. Default fits a section that gives the card
	 * roughly 8/12 of the container on desktop and full width on mobile.
	 */
	sizes?: string;
	className?: string;
}

export const StoreCard = forwardRef<HTMLDivElement, StoreCardProps>(
	({ address, hours, links, image, sizes = "(min-width: 1024px) 67vw, 100vw", className }, ref) => {
		return (
			<div
				ref={ref}
				className={twMerge(
					"flex w-full flex-col items-start gap-grid-component-spacing-md",
					className,
				)}
			>
				<div className="flex w-full items-end gap-grid-grid-gutter-sm">
					<div className="flex min-w-px flex-1 flex-col items-start gap-grid-component-spacing-5xs">
						<Typography
							variant="body-sm-reg"
							className="w-full leading-[1.5] text-text-dark-text-1"
						>
							{address}
						</Typography>
						{links?.map((link) => {
							const isPhone = link.variant === "phone";
							const openInNewTab = link.target === "_blank";
							return (
								<Link
									key={link.href}
									as={isPhone ? "a" : NextLink}
									href={link.href}
									variant="primary"
									size="medium"
									leftIcon={isPhone ? Call : ArrowOutward}
									target={openInNewTab ? "_blank" : undefined}
									rel={openInNewTab ? "noopener noreferrer" : undefined}
								>
									{link.label}
								</Link>
							);
						})}
					</div>
					{hours && hours.length > 0 && (
						<div className="flex min-w-px flex-1 flex-col items-end gap-grid-component-spacing-5xs">
							{hours.map((line, index) => (
								<Typography
									key={`${line}-${index}`}
									variant="body-sm-reg"
									className="w-full text-right leading-[1.5] text-text-dark-text-2"
								>
									{line}
								</Typography>
							))}
						</div>
					)}
				</div>
				<div className="relative h-[400px] w-full shrink-0 overflow-hidden bg-background-bg-image">
					{image && (
						<Image
							src={image.src}
							alt={image.alt ?? ""}
							fill
							sizes={sizes}
							className="pointer-events-none object-cover"
						/>
					)}
				</div>
			</div>
		);
	},
);

StoreCard.displayName = "StoreCard";
